import React, { useState, useEffect } from 'react'
import { api, formatCurrency, formatDate } from '../../utils/api'
import { getStageName, getSearchUrls, parseItems } from '../../utils/dealConstants'
import '../../styles/pages.css'

function QuoteExpiring() {
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(true)

  // Chase email states
  const [sendingChaseEmail, setSendingChaseEmail] = useState(null)
  const [selectedDeals, setSelectedDeals] = useState(new Set())
  const [sendingBatch, setSendingBatch] = useState(false)

  // Detail popup state
  const [selectedDeal, setSelectedDeal] = useState(null)

  useEffect(() => {
    loadDeals()
  }, [])

  async function loadDeals() {
    setLoading(true)
    try {
      const response = await api.get('/api/deals/expiring-quotes')
      setDeals(response.deals || [])
    } catch (error) {
      console.error('Failed to load deals:', error)
    } finally {
      setLoading(false)
    }
  }

  // Send chase email for single deal
  async function sendChaseEmail(dealId) {
    if (!confirm('Send quote expiring reminder email to this customer?')) return

    setSendingChaseEmail(dealId)
    try {
      await api.post(`/api/deals/${dealId}/chase-quote`)
      alert('Quote expiring email sent successfully!')
      setDeals(deals.filter(d => d.id !== dealId))
      setSelectedDeals(prev => {
        const newSet = new Set(prev)
        newSet.delete(dealId)
        return newSet
      })
    } catch (error) {
      alert(`Error sending email: ${error.message}`)
    } finally {
      setSendingChaseEmail(null)
    }
  }

  // Toggle selection
  function toggleSelection(dealId) {
    setSelectedDeals(prev => {
      const newSet = new Set(prev)
      if (newSet.has(dealId)) {
        newSet.delete(dealId)
      } else {
        newSet.add(dealId)
      }
      return newSet
    })
  }

  // Select all that have collection_link
  function selectAll() {
    const selectableIds = deals
      .filter(d => d.collection_link)
      .map(d => d.id)
    setSelectedDeals(new Set(selectableIds))
  }

  // Clear selection
  function clearSelection() {
    setSelectedDeals(new Set())
  }

  // Send batch chase emails
  async function sendBatchChaseEmails() {
    const selectedDealsList = deals.filter(d => selectedDeals.has(d.id) && d.collection_link)
    if (selectedDealsList.length === 0) {
      alert('No deals with quote links selected')
      return
    }

    if (!confirm(`Send quote expiring reminder to ${selectedDealsList.length} customer(s)?`)) return

    setSendingBatch(true)
    let successCount = 0
    let failCount = 0
    const sentIds = []

    for (const deal of selectedDealsList) {
      try {
        await api.post(`/api/deals/${deal.id}/chase-quote`)
        successCount++
        sentIds.push(deal.id)
      } catch {
        failCount++
      }
    }

    // Remove sent deals from list
    setDeals(prev => prev.filter(d => !sentIds.includes(d.id)))
    setSelectedDeals(new Set())

    if (failCount > 0) {
      alert(`Sent ${successCount} emails. ${failCount} failed.`)
    } else {
      alert(`Successfully sent ${successCount} reminder emails!`)
    }

    setSendingBatch(false)
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
        <div className="loading-spinner" />
      </div>
    )
  }

  if (deals.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-title">No Expiring Quotes</div>
        <div className="empty-state-description">
          There are no quotes expiring in the next 24-30 days.
        </div>
      </div>
    )
  }

  const selectableCount = deals.filter(d => d.collection_link).length
  const totalValue = deals.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0)
  const noLinkCount = deals.length - selectableCount

  // Calculate days since quote for urgency breakdown
  const urgentCount = deals.filter(d => {
    const daysSince = d.createdate
      ? Math.floor((Date.now() - new Date(d.createdate)) / (1000 * 60 * 60 * 24))
      : 0
    return daysSince >= 28
  }).length

  return (
    <>
      {/* KPI Stats */}
      <div className="statsRow">
        <div className="statBox statBox--amber">
          <div className="statBox__value">{deals.length}</div>
          <div className="statBox__label">Expiring Quotes</div>
        </div>
        <div className="statBox statBox--green">
          <div className="statBox__value">{formatCurrency(totalValue)}</div>
          <div className="statBox__label">Total Value</div>
        </div>
        <div className="statBox statBox--mint">
          <div className="statBox__value">{selectableCount}</div>
          <div className="statBox__label">Ready to Chase</div>
        </div>
        <div className="statBox statBox--rose">
          <div className="statBox__value">{noLinkCount}</div>
          <div className="statBox__label">No Collection Link</div>
        </div>
        <div className="statBox statBox--red">
          <div className="statBox__value">{urgentCount}</div>
          <div className="statBox__label">28+ Days (Urgent)</div>
        </div>
      </div>

      <div className="card">
      {/* Batch actions header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 'var(--space-3)',
        borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg-secondary)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <span className="text-muted">
            {deals.length} expiring quotes ({selectableCount} with collection link)
          </span>
          {selectedDeals.size > 0 && (
            <span className="badge badge-primary">{selectedDeals.size} selected</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {selectedDeals.size > 0 ? (
            <>
              <button className="btn btn-sm btn-secondary" onClick={clearSelection}>
                Clear
              </button>
              <button
                className="btn btn-sm btn-primary"
                onClick={sendBatchChaseEmails}
                disabled={sendingBatch}
              >
                {sendingBatch ? 'Sending...' : `Send ${selectedDeals.size} Emails`}
              </button>
            </>
          ) : (
            <button
              className="btn btn-sm btn-secondary"
              onClick={selectAll}
              disabled={selectableCount === 0}
            >
              Select All ({selectableCount})
            </button>
          )}
        </div>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 40 }}></th>
            <th>Deal Name</th>
            <th>Customer</th>
            <th>Quote Sent</th>
            <th>Days Since</th>
            <th>Value</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {deals.map(deal => {
            const daysSince = deal.createdate
              ? Math.floor((Date.now() - new Date(deal.createdate)) / (1000 * 60 * 60 * 24))
              : '-'
            const hasCollectionLink = !!deal.collection_link
            const isSelected = selectedDeals.has(deal.id)

            return (
              <tr
                key={deal.id}
                style={{
                  background: isSelected ? 'var(--color-primary-light)' : 'transparent',
                  cursor: 'pointer'
                }}
                onClick={() => setSelectedDeal(deal)}
              >
                <td onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelection(deal.id)}
                    disabled={!hasCollectionLink}
                    title={hasCollectionLink ? 'Select for batch send' : 'No collection link'}
                  />
                </td>
                <td>
                  <div style={{ fontWeight: 500 }}>{deal.dealname}</div>
                  {deal.parsed_items && (
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4, whiteSpace: 'pre-line', lineHeight: 1.4 }}>
                      {deal.parsed_items}
                    </div>
                  )}
                </td>
                <td>
                  <div>{deal.customer_name || '-'}</div>
                  <div className="text-sm text-muted">{deal.seller_email}</div>
                </td>
                <td>{formatDate(deal.createdate)}</td>
                <td>
                  <span className={`badge ${daysSince >= 28 ? 'badge-error' : 'badge-warning'}`}>
                    {daysSince} days
                  </span>
                </td>
                <td>{formatCurrency(deal.amount)}</td>
                <td>
                  {hasCollectionLink ? (
                    <span className="badge badge-success">Ready</span>
                  ) : (
                    <span className="badge badge-warning">No Link</span>
                  )}
                </td>
                <td onClick={e => e.stopPropagation()}>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => sendChaseEmail(deal.id)}
                    disabled={sendingChaseEmail === deal.id || !hasCollectionLink}
                    title={hasCollectionLink ? 'Send chase email' : 'No collection link available'}
                  >
                    {sendingChaseEmail === deal.id ? 'Sending...' : 'Chase Quote'}
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>

    {/* Deal Detail Modal */}
    {selectedDeal && (
      <div className="modal-overlay" onClick={() => setSelectedDeal(null)}>
        <div
          className="modal-content modal-large"
          onClick={e => e.stopPropagation()}
          style={{ maxHeight: '90vh', overflow: 'auto' }}
        >
          <div className="modal-header">
            <h2>{selectedDeal.dealname}</h2>
            <button className="modal-close" onClick={() => setSelectedDeal(null)}>&times;</button>
          </div>
          <div className="modal-body-scroll">
            {/* Deal info grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
              {/* Left column */}
              <div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Deal ID</label>
                  <div>{selectedDeal.id}</div>
                </div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Customer</label>
                  <div>{selectedDeal.customer_name || '-'}</div>
                  <div className="text-sm">{selectedDeal.seller_email}</div>
                </div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Quote Sent</label>
                  <div>{formatDate(selectedDeal.createdate)}</div>
                </div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Value</label>
                  <div>{formatCurrency(selectedDeal.amount)}</div>
                </div>
              </div>

              {/* Right column */}
              <div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Days Since Quote</label>
                  <div>
                    <span className={`badge ${Math.floor((Date.now() - new Date(selectedDeal.createdate)) / (1000 * 60 * 60 * 24)) >= 28 ? 'badge-error' : 'badge-warning'}`}>
                      {Math.floor((Date.now() - new Date(selectedDeal.createdate)) / (1000 * 60 * 60 * 24))} days
                    </span>
                  </div>
                </div>
                {selectedDeal.last_chased_date && (
                  <div style={{ marginBottom: 'var(--space-3)' }}>
                    <label className="text-muted" style={{ fontSize: '0.75rem' }}>Last Chased</label>
                    <div>{formatDate(selectedDeal.last_chased_date)}</div>
                  </div>
                )}
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Collection Link</label>
                  <div>
                    {selectedDeal.collection_link ? (
                      <span className="badge badge-success">Available</span>
                    ) : (
                      <span className="badge badge-warning">Not Generated</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Parsed Items with search buttons */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <h4 style={{ margin: '0 0 var(--space-2) 0' }}>Items</h4>
              {(() => {
                const items = parseItems(selectedDeal?.parsed_items)
                if (items.length === 0) return <span className="text-muted">No items</span>

                return (
                  <div style={{ fontSize: '0.85rem' }}>
                    {items.map((item, index) => {
                      const urls = getSearchUrls(item)
                      return (
                        <div key={index} style={{
                          marginBottom: 8,
                          padding: 8,
                          background: 'var(--color-bg-secondary)',
                          borderRadius: 4
                        }}>
                          <div style={{ marginBottom: 4 }}>{item}</div>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-xs btn-secondary" onClick={() => window.open(urls.reverbSold, '_blank')} title="Reverb Sold">RS</button>
                            <button className="btn btn-xs btn-secondary" onClick={() => window.open(urls.reverbActive, '_blank')} title="Reverb Active">RA</button>
                            <button className="btn btn-xs btn-secondary" onClick={() => window.open(urls.ebayActive, '_blank')} title="eBay Active">EA</button>
                            <button className="btn btn-xs btn-secondary" onClick={() => window.open(urls.ebaySold, '_blank')} title="eBay Sold">ES</button>
                            <button className="btn btn-xs btn-secondary" onClick={() => window.open(urls.google, '_blank')} title="Google">G</button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )
              })()}
            </div>

            {/* Collection Link */}
            {selectedDeal.collection_link && (
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <label className="text-muted" style={{ fontSize: '0.75rem' }}>Collection Link</label>
                <div>
                  <a href={selectedDeal.collection_link} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                    {selectedDeal.collection_link}
                  </a>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer" style={{ padding: 'var(--space-4)' }}>
            <button className="btn btn-secondary" onClick={() => setSelectedDeal(null)}>Close</button>
            {selectedDeal.collection_link && (
              <button
                className="btn btn-primary"
                onClick={() => {
                  sendChaseEmail(selectedDeal.id)
                  setSelectedDeal(null)
                }}
                disabled={sendingChaseEmail === selectedDeal.id}
              >
                {sendingChaseEmail === selectedDeal.id ? 'Sending...' : 'Chase Quote'}
              </button>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  )
}

export default QuoteExpiring
